import { useEffect, useRef } from "react";
import GoogleMap from "google-map-react";
import { GOOGLE_HARITA_ANAHTARI } from "../../../../helpers/haritaAnahtari";
import { SeferGuzergahData } from "../../../../models/response/ambarVoyage/SeferGuzergahResponse";
import { kmMetni } from "../../../../helpers/seferFiyat";
import { sureMetni } from "../../../../helpers/seferGuzergahi";

interface Props {
    veri: SeferGuzergahData
    onGeri: () => void
}

const ROTA_RENGI = '#e8590c';
const DEPO_RENGI = '#17c653';

/**
 * Sefer guzergahi haritasi (15.10.2026): yukleme noktasi (D), sira numarali teslim duraklari ve sunucunun verdigi
 * gercek yol cizgisi (bacak bacak). Yanda durak listesi, altta toplam KM ve sure. Geri yukler tablosuna doner.
 */
const GuzergahHaritasi = ({ veri, onGeri }: Props) => {
    const harita = useRef<{ map: any, maps: any } | null>(null);
    const cizimler = useRef<any[]>([]);

    const ciz = () => {
        if (!harita.current)
            return;

        const { map, maps } = harita.current;
        cizimler.current.forEach(x => x.setMap(null));
        cizimler.current = [];

        const sinir = new maps.LatLngBounds();
        const genislet = (lat: number, lng: number) => sinir.extend(new maps.LatLng(lat, lng));

        veri.cizgiler.forEach(bacak => {
            const yol = bacak.map(([lat, lng]) => ({ lat, lng }));
            yol.forEach(p => genislet(p.lat, p.lng));
            cizimler.current.push(new maps.Polyline({ path: yol, strokeColor: ROTA_RENGI, strokeOpacity: 0.9, strokeWeight: 4, map }));
        });

        const isaret = (lat: number, lng: number, metin: string, renk: string, baslik: string) => {
            genislet(lat, lng);
            cizimler.current.push(new maps.Marker({
                position: { lat, lng },
                map,
                title: baslik,
                label: { text: metin, color: '#ffffff', fontSize: '11px', fontWeight: '700' },
                icon: { path: maps.SymbolPath.CIRCLE, scale: 11, fillColor: renk, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 },
            }));
        };

        if (veri.baslangicLat != null && veri.baslangicLon != null)
            isaret(Number(veri.baslangicLat), Number(veri.baslangicLon), 'D', DEPO_RENGI, veri.baslangicAdi ?? 'Yükleme');

        veri.duraklar.forEach(d => {
            if (d.lat != null && d.lon != null)
                isaret(Number(d.lat), Number(d.lon), String(d.sira), ROTA_RENGI, `${d.sira}. ${d.teslimFirmasi ?? ''}`);
        });

        if (!sinir.isEmpty())
            map.fitBounds(sinir, 40);
    }

    useEffect(() => { ciz(); }, [veri]);

    const konumlu = veri.duraklar.some(d => d.lat != null && d.lon != null);

    return (
        <div>
            <div className="d-flex align-items-center mb-5">
                <button className="btn btn-sm btn-light btn-active-light-primary me-4" onClick={onGeri}>
                    <i className="fa fa-arrow-left me-2" />Geri
                </button>
                <h4 className="text-black m-0 flex-grow-1">Güzergah</h4>
                <span className="text-muted fs-7">Gerçek karayolu · yükleme noktasından son teslime</span>
            </div>

            <div className="row">
                <div className="col-lg-8 mb-5">
                    <div style={{ height: 480, borderRadius: 8, overflow: 'hidden', border: '1px solid #e4e6ef', position: 'relative' }}>
                        <GoogleMap
                            defaultCenter={{ lat: 39.0, lng: 35.0 }}
                            defaultZoom={6}
                            bootstrapURLKeys={{ key: GOOGLE_HARITA_ANAHTARI }}
                            yesIWantToUseGoogleMapApiInternals
                            onGoogleApiLoaded={({ map, maps }) => { harita.current = { map, maps }; ciz(); }}
                            options={{ streetViewControl: false, clickableIcons: false, mapTypeControl: false }}
                        />
                        {
                            !konumlu && (
                                <div className="position-absolute top-50 start-50 translate-middle bg-white rounded px-4 py-3 text-muted">
                                    Haritada gösterilecek konum yok.
                                </div>
                            )
                        }
                    </div>
                </div>

                <div className="col-lg-4">
                    <div className="table-responsive">
                        <table className="table align-middle table-row-dashed mb-0">
                            <tbody className="text-gray-600 fw-bold">
                                <tr>
                                    <td style={{ width: 34 }}><span className="badge badge-success">D</span></td>
                                    <td>{veri.baslangicAdi ?? 'Yükleme noktası'}<div className="text-muted fs-8">Yükleme</div></td>
                                    <td></td>
                                </tr>
                                {
                                    veri.duraklar.map(d =>
                                        <tr key={d.ambarId}>
                                            <td><span className="badge" style={{ background: ROTA_RENGI, color: '#fff' }}>{d.sira}</span></td>
                                            <td>
                                                {d.teslimFirmasi}
                                                {d.elleIlkSira && <span className="badge badge-light-warning ms-2">İlk teslim</span>}
                                                <div className="text-muted fs-8">{d.refNo}{d.ilIlce ? ' · ' + d.ilIlce : ''}{d.lat == null || d.lon == null ? ' · konum yok' : ''}</div>
                                            </td>
                                            <td className="custom-text-right text-nowrap">
                                                {kmMetni(d.km)}
                                                {d.sureDk != null && <div className="text-muted fs-8">{sureMetni(d.sureDk)}</div>}
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="d-flex flex-wrap gap-8 mt-2">
                <div><div className="text-muted fs-8 text-uppercase">Toplam yol</div><div className="fs-4 fw-bolder">{kmMetni(veri.toplamKm)}</div></div>
                <div><div className="text-muted fs-8 text-uppercase">Sürüş süresi</div><div className="fs-4 fw-bolder">{sureMetni(veri.toplamSureDk)}</div></div>
                <div><div className="text-muted fs-8 text-uppercase">Teslim</div><div className="fs-4 fw-bolder">{veri.duraklar.length} durak</div></div>
            </div>
        </div>
    )
}

export default GuzergahHaritasi;
